/**
 * Liste de choses à faire, cochées d'elles-mêmes une fois faites (mise en
 * route de l'entreprise, premiers pas d'un compte) : la progression en tête
 * (« 4 sur 6 » et une barre), puis une ligne par étape. Une étape à faire
 * ouvre la page qui la règle ; une étape faite est cochée, sans lien.
 * `compact` ne garde que la progression et un geste (la carte de l'accueil
 * qui mène à la page de la liste) ; `hideDone` retire les étapes faites de la
 * liste sans changer la progression (la page de la liste).
 */
import type { ReactNode } from 'react';
import { CheckCircle2, Circle } from 'lucide-react';
import { RecordIcon, RecordList, RecordRow } from '../RecordRow.js';
import { Badge } from './Badge.js';
import { ActionButton, type Action } from './action.js';

export interface ChecklistItem {
  id: string;
  label: string;
  hint?: string;
  done: boolean;
  /** Page qui règle l'étape. */
  to?: string;
  /** À la place d'un lien : un geste qui enregistre avant d'ouvrir la page. */
  onOpen?: () => void;
}

export function Checklist(props: {
  label: string;
  items: readonly ChecklistItem[];
  /** « 4 sur 6 », affiché. */
  progress: string;
  /** « 4 réglages faits sur 6 », lu par les lecteurs d'écran. */
  progressLabel: string;
  doneLabel: string;
  compact?: boolean;
  hideDone?: boolean;
  /** Avec `compact` : le geste qui ouvre la liste. */
  action?: Action;
}): ReactNode {
  const done = props.items.filter((item) => item.done).length;
  const total = props.items.length;
  const shown = props.hideDone ? props.items.filter((item) => !item.done) : props.items;
  const ratio = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div className="ui-checklist">
      <div className="ui-checklist__progress">
        <span className="ui-checklist__count" aria-hidden="true">{props.progress}</span>
        <span
          className="ui-checklist__bar"
          role="progressbar"
          aria-label={props.progressLabel}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={done}
          style={{ '--progress': `${ratio}%` }}
        />
      </div>
      {props.compact ? (props.action ? <ActionButton action={props.action} variant="secondary" /> : null) : (
        <RecordList label={props.label}>
          {shown.map((item) => (
            <li key={item.id} className={item.done ? 'ui-checklist__item ui-checklist__item--done' : 'ui-checklist__item'}>
              <RecordRow
                {...(!item.done && item.onOpen ? { onOpen: item.onOpen } : !item.done && item.to ? { to: item.to } : {})}
                lead={(
                  <RecordIcon>
                    {item.done ? <CheckCircle2 size={20} aria-hidden="true" /> : <Circle size={20} aria-hidden="true" />}
                  </RecordIcon>
                )}
                title={item.label}
                lines={item.hint ? [item.hint] : []}
                badge={item.done ? <Badge tone="ok">{props.doneLabel}</Badge> : undefined}
              />
            </li>
          ))}
        </RecordList>
      )}
    </div>
  );
}
